<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="xl"
            ok-only
            ok-title="Close"
            @close="hidden"
            @hidden="hidden"
        >
            <template #modal-title>
                Manage Vocabulary Cards
            </template>

            <div v-if="loading" class="d-flex my-2">
                <b-spinner label="Loading..." class="m-auto" />
            </div>
            <div v-else-if="vocabulary_list.length === 0" class="alert alert-warning">
                No vocabulary terms in this publication. Add terms before generating cards.
            </div>
            <template v-else>
                <div class="alert alert-warning"
                     v-if="publication.allow_vocab_cards && needs_new_card && !updating_pdf"
                >
                    Vocabulary has been added/updated since most recent PDF generation.
                    Toggle <em>Enable Vocabulary Cards</em> to regenerate the PDF.
                </div>
                <div class="text-right mb-4">
                    <b-form-checkbox
                        :checked="publication.allow_vocab_cards"
                        switch
                        @change="toggleAllowVocabCards"
                    >
                        Enable Vocabulary Cards
                    </b-form-checkbox>
                </div>
                <div class="row">
                    <div class="col-7">
                        <div class="d-flex">
                            <h5>Vocabulary List</h5>
                            <b-form-checkbox
                                v-if="unfit_vocab_list.length > 0"
                                :checked="show_unfit"
                                @change="show_unfit = !show_unfit"
                                switch
                                class="ml-auto"
                            >
                                List Excluded Terms
                            </b-form-checkbox>
                        </div>
                        <hr />
                        <b-table
                            class="vocabulary-list"
                            :items="show_unfit ? unfit_vocab_list : vocabulary_list"
                            :fields="fields"
                            striped
                            responsive
                            sticky-header
                            outlined
                            no-border-collapse
                            head-variant="light"
                        >
                            <template v-slot:head(term)>
                                Term
                            </template>
                            <template v-slot:head(unit_num)>
                                Unit
                            </template>
                            <template v-slot:head(week_num)>
                                Week
                            </template>
                            <template v-slot:head(article_title)>
                                Article
                            </template>
                            <template v-slot:head(is_limit_exceeded)>Errors</template>

                            <template v-slot:cell(term)="data">
                                <font-awesome-icon
                                    v-if="data.item.is_limit_exceeded"
                                    class="text-warning"
                                    :icon="['fas', 'exclamation-triangle']"
                                    fixed-width
                                    content="This vocabulary item has exceeded a length limit and cannot be added."
                                    v-tippy="{placement: 'bottom'}"
                                />
                                {{ data.item.term }}
                            </template>
                        </b-table>
                    </div>
                    <div class="col-5">
                        <h5>PDF</h5>
                        <hr />
                        <div class="mb-5">
                            <template v-if="updating_pdf !== null">
                                <div class="text-center">
                                    <b-spinner />
                                    <div>Generating document..</div>
                                </div>
                            </template>
                            <div v-else-if="vocab_card_url !== null">
                                <div class="text-center">
                                    <b-btn
                                        class="mb-2"
                                        variant="outline-primary"
                                        :href="vocab_card_url"
                                        target="_blank"
                                    >
                                        <font-awesome-icon
                                            :icon="['fad', 'external-link']"
                                            fixed-width
                                        />
                                        Open in new tab
                                    </b-btn>
                                </div>

                                <embed
                                    class="pdf-container"
                                    width="100%"
                                    height="100%"
                                    :src="vocab_card_url"
                                >
                            </div>
                            <div class="text-center" v-else>
                                <p>No PDF has been generated.</p>
                                Click <em>Enable Vocabulary Cards</em> to generate one.
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </b-modal>
    </div>
</template>

<style lang="scss" scoped>
.vocabulary-list {
    max-height: 54vh;
}
</style>

<script>
export default {
    data: () => ({
        loading: true,
        updating_vocab_card: false,
        show_unfit: false,
        vocab_card_url: null,
        vocabulary_list: [],
        updating_pdf: null,
        fields: [
            {
                key: 'term',
                sortable: true
            },
            {
                key: 'unit_num',
                sortable: true
            },
            {
                key: 'week_num',
                sortable: true
            },
            {
                key: 'article_title',
                sortable: true
            },
        ],

    }),
    props: [
        'value',
        'publication',
    ],
    computed: {
        modal_id() {
            return 'manage-vocab_cards-modal'
        },
        needs_new_card() {
            return this.vocabulary_list.filter((term) => term.is_new_vocab === true).length > 0
        },
        unfit_vocab_list() {
            return this.vocabulary_list.filter((term) => term.is_limit_exceeded === true)
        },
    },
    methods: {
        async toggleAllowVocabCards(allow_vocab_cards) {
            try {
                this.updating_vocab_card = true
                const payload = {allow_vocab_cards}
                await this.axios.post(`/api/publications/${this.publication.id}/vocab_cards`, payload)
                this.$emit('setAllowVocabCard', allow_vocab_cards)

                // poll to see if the pdf has been updated, every 5 seconds
                if (allow_vocab_cards === true && this.updating_pdf === null) {
                    this.updating_pdf = window.setInterval(this.checkForUpdatedPdf, 5000)
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to toggle Allow Vocab Cards: ${e.message}`,
                    }
                })
            } finally {
                this.updating_vocab_card = false
            }
        },
        async getVocabCardsInfo() {
            this.loading = true
            try {
                const response = await this.axios.get(`/api/publications/${this.publication.id}/vocab_cards`)
                const {
                    allow_vocab_cards,
                    vocab_card_url,
                    vocabulary_list,
                } = response.data

                this.vocab_card_url = vocab_card_url
                this.vocabulary_list = vocabulary_list
                this.$set(this.publication, 'allow_vocab_cards', allow_vocab_cards)
            } catch (e) {
                await this.$swal({
                    html: `There was a problem loading vocabulary reports.`,
                    allowOutsideClick: false,
                })
            } finally {
                this.loading = false
            }
        },
        async checkForUpdatedPdf() {
            const response = await this.axios.get(`/api/publications/${this.publication.id}/vocab_cards`)
            const {
                vocab_card_url,
                vocabulary_list
            } = response.data
            // Because generated files are unique, we can rely on the URL to determine if the PDF has been updated.
            if (vocab_card_url !== this.vocab_card_url) {
                this.vocab_card_url = vocab_card_url
                this.vocabulary_list = vocabulary_list
                window.clearInterval(this.updating_pdf)
                this.updating_pdf = null
            }
        },
        hidden() {
            this.$emit('hide')
        },
    },
    async mounted() {
        await this.getVocabCardsInfo()
    }
}
</script>
